import React, { useEffect, useState } from 'react';
import { useI18n } from '../context/i18n';

const SHOPIFY_AUTH_URL = process.env.REACT_APP_SHOPIFY_AUTH_URL || 'https://auth.upsellerate.co';

const progressMessages = {
  loading: 'Connecting securely to Shopify...',
  workspace: 'Creating your UPSELLerate workspace...',
};

const frontendRequestId = () => Math.random().toString(16).slice(2, 10);

const logShopifyEntryUi = ({
  request_id,
  shop,
  company_id,
  dataset_name,
  stage,
  status,
  duration_ms,
  ...fields
}) => {
  const parts = [
    '[shopify-entry-ui]',
    `request_id=${request_id || '-'}`,
    `shop=${shop || '-'}`,
    `company_id=${company_id || '-'}`,
    `dataset=${dataset_name || '-'}`,
    `stage=${stage}`,
    `status=${status}`,
  ];
  if (duration_ms !== undefined) parts.push(`duration_ms=${duration_ms}`);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) parts.push(`${key}=${value}`);
  });
  console.log(parts.join(' '));
};

function ShopifyComplete() {
  const { setLanguage } = useI18n();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState(progressMessages.loading);

  useEffect(() => {
    const requestId = frontendRequestId();
    const totalStart = performance.now();
    logShopifyEntryUi({ request_id: requestId, stage: 'complete_mounted', status: 'started' });
    const params = new URLSearchParams(window.location.search);
    const ott = params.get('ott');

    if (!ott) {
      logShopifyEntryUi({ request_id: requestId, stage: 'ott_missing', status: 'missing', duration_ms: Math.round((performance.now() - totalStart) * 10) / 10 });
      const hasCompletedExchange = localStorage.getItem('shopifyInitialSync') || (
        localStorage.getItem('token') && localStorage.getItem('shopifyCompanyId')
      );
      if (hasCompletedExchange) {
        logShopifyEntryUi({ request_id: requestId, stage: 'redirect_home', status: 'existing_session' });
        logShopifyEntryUi({ request_id: requestId, stage: 'complete_total', status: 'existing_session', duration_ms: Math.round((performance.now() - totalStart) * 10) / 10 });
        window.location.replace('/');
        return;
      }
      setStatus('error');
      setMessage('Missing Shopify login token. Please reconnect from Shopify.');
      return;
    }
    logShopifyEntryUi({ request_id: requestId, stage: 'ott_found', status: 'success' });

    const exchangeAndSync = async () => {
      try {
        const exchangeStart = performance.now();
        logShopifyEntryUi({ request_id: requestId, stage: 'exchange_request_start', status: 'started' });
        const exchangeResponse = await fetch(`${SHOPIFY_AUTH_URL}/auth/shopify/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ott }),
        });
        const exchangeData = await exchangeResponse.json();

        if (!exchangeResponse.ok || !exchangeData.access_token) {
          logShopifyEntryUi({
            request_id: requestId,
            shop: exchangeData.shop,
            company_id: exchangeData.company_id,
            dataset_name: exchangeData.dataset_name,
            stage: 'exchange_response_error',
            status: exchangeResponse.status,
            duration_ms: Math.round((performance.now() - exchangeStart) * 10) / 10,
          });
          throw new Error(exchangeData.error || 'Unable to complete Shopify login');
        }
        logShopifyEntryUi({
          request_id: requestId,
          shop: exchangeData.shop,
          company_id: exchangeData.company_id,
          dataset_name: exchangeData.dataset_name,
          stage: 'exchange_response_success',
          status: exchangeResponse.status,
          duration_ms: Math.round((performance.now() - exchangeStart) * 10) / 10,
          initial_sync_required: exchangeData.initial_sync_required === true,
        });

        const storageStart = performance.now();
        logShopifyEntryUi({
          request_id: requestId,
          shop: exchangeData.shop,
          company_id: exchangeData.company_id,
          dataset_name: exchangeData.dataset_name,
          stage: 'session_storage_start',
          status: 'started',
        });
        localStorage.setItem('token', exchangeData.access_token);
        localStorage.setItem('projectId', exchangeData.project_id || 'primeval-gear-417120');
        localStorage.setItem('companyId', exchangeData.company_id);
        localStorage.setItem('user', exchangeData.username || `shopify:${exchangeData.company_id}`);
        localStorage.setItem('datasetId', exchangeData.dataset_name);
        localStorage.setItem('shopifyCompanyId', exchangeData.company_id);
        localStorage.setItem('shopifyShop', exchangeData.shop || '');
        localStorage.setItem('language', 'en');
        setLanguage('en');
        localStorage.setItem('userEmail', exchangeData.email || '');
        localStorage.setItem('displayName', exchangeData.display_name || '');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('currentConversationId');
        logShopifyEntryUi({
          request_id: requestId,
          shop: exchangeData.shop,
          company_id: exchangeData.company_id,
          dataset_name: exchangeData.dataset_name,
          stage: 'session_storage_end',
          status: 'success',
          duration_ms: Math.round((performance.now() - storageStart) * 10) / 10,
        });

        logShopifyEntryUi({
          request_id: requestId,
          shop: exchangeData.shop,
          company_id: exchangeData.company_id,
          dataset_name: exchangeData.dataset_name,
          stage: 'initial_sync_required_evaluated',
          status: exchangeData.initial_sync_required === true ? 'true' : 'false',
        });
        if (exchangeData.initial_sync_required === true) {
          localStorage.setItem('shopifyInitialSync', JSON.stringify({
            status: 'pending',
            shop: exchangeData.shop || '',
            company_id: exchangeData.company_id,
            dataset_name: exchangeData.dataset_name,
            started_at: Date.now(),
          }));
          logShopifyEntryUi({
            request_id: requestId,
            shop: exchangeData.shop,
            company_id: exchangeData.company_id,
            dataset_name: exchangeData.dataset_name,
            stage: 'shopify_initial_sync_storage_set',
            status: 'success',
          });
        } else {
          localStorage.removeItem('shopifyInitialSync');
          logShopifyEntryUi({
            request_id: requestId,
            shop: exchangeData.shop,
            company_id: exchangeData.company_id,
            dataset_name: exchangeData.dataset_name,
            stage: 'shopify_initial_sync_storage_removed',
            status: 'success',
          });
        }
        setMessage(progressMessages.workspace);
        logShopifyEntryUi({
          request_id: requestId,
          shop: exchangeData.shop,
          company_id: exchangeData.company_id,
          dataset_name: exchangeData.dataset_name,
          stage: 'redirect_home',
          status: 'success',
        });
        logShopifyEntryUi({
          request_id: requestId,
          shop: exchangeData.shop,
          company_id: exchangeData.company_id,
          dataset_name: exchangeData.dataset_name,
          stage: 'complete_total',
          status: 'success',
          duration_ms: Math.round((performance.now() - totalStart) * 10) / 10,
        });
        window.location.replace('/');
      } catch (error) {
        console.error('Shopify completion failed:', error);
        logShopifyEntryUi({
          request_id: requestId,
          stage: 'complete_total',
          status: 'error',
          duration_ms: Math.round((performance.now() - totalStart) * 10) / 10,
          error_type: error?.name || 'Error',
          error_message: error?.message || 'unknown',
        });
        setStatus('error');
        setMessage(error.message || 'Unable to complete Shopify login. Please reconnect from Shopify.');
      }
    };

    exchangeAndSync();
  }, [setLanguage]);

  const isWorking = status === 'loading';

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      background: '#F5F7FB',
      color: '#121C2A',
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif'
    }}>
      <div style={{
        width: '100%',
        maxWidth: 540,
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        padding: 34,
        textAlign: 'center',
        boxShadow: '0 16px 40px rgba(18, 28, 42, 0.08)'
      }}>
        <img
          src="https://upsellerate.ai/logo-upsellerate-transparent.png"
          alt="UPSELLerate AI"
          style={{ height: 46, width: 'auto', marginBottom: 24 }}
        />
        <h1 style={{ fontSize: 24, margin: '0 0 10px', fontWeight: 800 }}>
          {status === 'error' ? 'Shopify connection issue' : 'Shopify connected'}
        </h1>
        <p style={{ color: '#4B5563', fontSize: 15, lineHeight: 1.6, margin: '0 auto', maxWidth: 440 }}>
          {message}
        </p>

        {isWorking && (
          <div style={{ marginTop: 28 }}>
            <div style={{
              width: 44,
              height: 44,
              border: '4px solid #E9D8FD',
              borderTopColor: '#710FE5',
              borderRadius: '50%',
              margin: '0 auto 18px',
              animation: 'upsellerate-spin 1s linear infinite'
            }} />
            <div style={{ color: '#710FE5', fontSize: 13, fontWeight: 800 }}>
              {message}
            </div>
          </div>
        )}

        {status === 'error' && (
          <a
            href="https://apps.shopify.com/"
            style={{ display: 'inline-block', marginTop: 22, color: '#710FE5', fontWeight: 800, textDecoration: 'none' }}
          >
            Return to Shopify
          </a>
        )}
      </div>
      <style>{`
        @keyframes upsellerate-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ShopifyComplete;
